import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  InteractionStatus,
  AccountInfo,
  EventType,
  EventCallbackFunction,
} from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';
import { SetAADUserWithClaims } from '@et/guards';
import {
  initTracking,
  setUser,
  setUserFromSelfProfile,
  trackEv,
  trackingEvents,
} from '@et/utils';
import { Store } from '@ngxs/store';
import { filter, Subject, takeUntil } from 'rxjs';
import { ProfileV2ApiService } from '@et/api';

@Component({
  selector: 'et-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private msalBroadcastService = inject(MsalBroadcastService);
  private msal = inject(MsalService);
  private store = inject(Store);
  private profileV2Api = inject(ProfileV2ApiService);

  private readonly _onDestroy$ = new Subject<void>();

  ngOnInit(): void {
    initTracking();

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        const [account] = this.msal.instance.getAllAccounts();
        if (account) {
          this.store.dispatch(new SetAADUserWithClaims(account));
          this.profileV2Api.getSelfProfile().subscribe({
            next: (self) => setUserFromSelfProfile(self, account.username),
            error: () => setUser(account),
          });
          trackEv(trackingEvents.webEJournalLogIn, {
            platform: Capacitor.getPlatform(),
          });
        }
      });
    this.setMsalInstaceCallback();
  }

  /**
   * Sets the active account after a successful login redirect
   */
  private setMsalInstaceCallback() {
    const callback = (event: {
      eventType: EventType;
      payload: { account: AccountInfo };
    }) => {
      // set active account after redirect
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload.account
      ) {
        const account = event.payload.account;
        this.msal.instance.setActiveAccount(account);

        this.msal.loginRedirect();
      }
    };
    this.msal.instance.addEventCallback(callback as EventCallbackFunction);
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
